import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, ExternalLink, Eye, EyeOff, Star, GripVertical, ChevronDown, AlertTriangle } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";
import RichTextEditor from "./RichTextEditor";
import TemplatePicker from "./TemplatePicker";
import { isHtml, shorthandToHtml } from "../../lib/richText";
import { toast } from "sonner";

const SECTIONS = [
  { key: "client", label: "The Client", hint: "Who they are and what they do." },
  { key: "challenge", label: "The Challenge", hint: "What wasn't working and why it mattered." },
  { key: "solution", label: "The Solution", hint: "What CASS changed and how we helped." },
  { key: "difference", label: "The Difference", hint: "What changed within the business." },
  { key: "outcome", label: "The Outcome", hint: "What this ultimately meant for the client." },
  { key: "closing", label: "Closing summary", hint: "Optional wrap-up. Leave the heading blank to show as plain text." },
];

const empty = {
  title: "", client_name: "", industry: "", location: "", image: "", image_alt: "", logo: "",
  subheading: "", summary: "",
  section_bodies: {}, section_headings: {},
  results: [], testimonials: [], pull_quote: "", quote_by: "",
  video_url: "", video_upload: "", service_slugs: [],
  cta_heading: "", cta_text: "", cta_button: "", cta_link: "",
  seo_title: "", meta_description: "", canonical_url: "", og_title: "", og_description: "", og_image: "", noindex: false,
  featured: false, published: true, order: 0,
  quote: "", content: [], result: "",
};

export default function CaseStudiesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const load = () => adminApi.caseStudies.list().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...empty, order: items.length + 1 });
  const remove = async (id) => {
    if (!window.confirm("Delete this success story?")) return;
    await adminApi.caseStudies.remove(id);
    toast.success("Deleted");
    load();
  };

  const handleDrop = async (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setItems(reordered);
    setDragIndex(null);
    try { await adminApi.caseStudiesReorder(reordered.map((i) => i.id)); toast.success("Order updated"); load(); }
    catch { toast.error("Couldn't save order"); load(); }
  };

  if (editing) return <Editor item={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">Client Success Stories</h1>
        <button data-testid="case-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"><Plus size={16} /> Add story</button>
      </div>
      <p className="text-slate-500 text-sm mb-8">Detailed client case studies shown on the Testimonials page (and the latest few on Insights). Each opens its own page at <code>/success-stories/&lt;slug&gt;</code>. <span className="inline-flex items-center gap-1 text-slate-400"><GripVertical size={13} /> Drag rows to reorder.</span></p>

      <div className="space-y-3">
        {items.length === 0 && !loading && <p className="text-sm text-slate-400">No success stories yet.</p>}
        {items.map((c, index) => (
          <div key={c.id} data-testid={`case-row-${c.id}`}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
            onDrop={() => handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            className={`flex items-center gap-4 bg-white rounded-2xl border p-4 shadow-sm transition-all ${overIndex === index && dragIndex !== null ? "border-orange ring-2 ring-orange/30" : "border-slate-100"} ${dragIndex === index ? "opacity-50" : ""}`}>
            <button draggable onDragStart={() => setDragIndex(index)} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy shrink-0 touch-none" title="Drag to reorder" data-testid={`case-drag-${c.id}`}><GripVertical size={16} /></button>
            <div className="w-16 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
              {c.image ? <img src={mediaUrl(c.image, 200)} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-300 text-xs">No image</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy truncate">{c.title || "Untitled"}</p>
              <p className="text-xs mt-0.5 text-slate-400">{c.client_name || "—"}{c.industry ? ` · ${c.industry}` : ""} · Order {c.order}
                {c.featured && <span className="ml-2 text-orange font-semibold inline-flex items-center gap-1"><Star size={12} fill="currentColor" /> Featured</span>}
                {c.published ? <span className="ml-2 text-green-600 font-semibold inline-flex items-center gap-1"><Eye size={12} /> Published</span> : <span className="ml-2 text-amber-500 font-semibold inline-flex items-center gap-1"><EyeOff size={12} /> Draft</span>}
              </p>
            </div>
            <a href={`/success-stories/${c.slug}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-orange p-2"><ExternalLink size={15} /></a>
            <button onClick={() => setEditing(c)} className="text-sm font-semibold text-orange hover:text-orange-dark px-3 py-1.5">Edit</button>
            <button data-testid={`case-delete-${c.id}`} onClick={() => remove(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Editor({ item, onDone, onCancel }) {
  const [form, setForm] = useState(() => {
    const init = { ...empty, ...item };
    const sb = { ...(init.section_bodies || {}) };
    Object.keys(sb).forEach((k) => { if (sb[k] && !isHtml(sb[k])) sb[k] = shorthandToHtml(sb[k]); });
    return { ...init, section_bodies: sb };
  });
  const [services, setServices] = useState([]);
  const [seoOpen, setSeoOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { adminApi.services.list().then(setServices).catch(() => setServices([])); }, []);

  const setBody = (key, v) => setForm((f) => ({ ...f, section_bodies: { ...f.section_bodies, [key]: v } }));
  const setHeading = (key, v) => setForm((f) => ({ ...f, section_headings: { ...f.section_headings, [key]: v } }));

  const toggleService = (slug) => setForm((f) => {
    const cur = f.service_slugs || [];
    return { ...f, service_slugs: cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug] };
  });

  // Results
  const addResult = () => set("results", [...(form.results || []), { heading: "", explanation: "", stat: "" }]);
  const updResult = (i, k, v) => set("results", form.results.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rmResult = (i) => set("results", form.results.filter((_, idx) => idx !== i));

  // Testimonials
  const addTestimonial = () => set("testimonials", [...(form.testimonials || []), { quote: "", name: "", job_title: "", company: "", featured: (form.testimonials || []).length === 0 }]);
  const updTestimonial = (i, k, v) => set("testimonials", form.testimonials.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const setFeaturedTestimonial = (i) => set("testimonials", form.testimonials.map((t, idx) => ({ ...t, featured: idx === i })));
  const rmTestimonial = (i) => set("testimonials", form.testimonials.filter((_, idx) => idx !== i));

  // Non-blocking pre-publish warnings
  const warnings = [];
  if (!form.seo_title && !form.title) warnings.push("SEO title (falls back to the story title)");
  if (!form.meta_description && !form.summary) warnings.push("Meta description");
  if (!form.image) warnings.push("Featured image");
  if (form.image && !form.image_alt) warnings.push("Featured image alt text");
  if (!(form.section_bodies?.challenge)) warnings.push("The Challenge");
  if (!(form.section_bodies?.solution)) warnings.push("The Solution");
  if (!(form.section_bodies?.outcome)) warnings.push("The Outcome");
  if (!form.cta_button) warnings.push("Call-to-action (using default)");

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    try {
      if (item.id) await adminApi.caseStudies.update(item.id, payload);
      else await adminApi.caseStudies.create(payload);
      toast.success("Success story saved");
      onDone();
    } catch { toast.error("Could not save"); setSaving(false); }
  };

  const titleLen = (form.seo_title || "").length;
  const metaLen = (form.meta_description || "").length;

  return (
    <div>
      <button onClick={onCancel} className="text-sm font-semibold text-slate-500 hover:text-navy mb-4 inline-flex items-center gap-1"><X size={15} /> Back</button>
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="font-display font-extrabold text-2xl text-navy">{item.id ? "Edit success story" : "New success story"}</h1>
        {item.slug && <a href={`/success-stories/${item.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-orange inline-flex items-center gap-1.5" data-testid="case-preview-link"><ExternalLink size={14} /> Preview page</a>}
      </div>

      {warnings.length > 0 && (
        <div className="max-w-2xl mb-5 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3" data-testid="case-warnings">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">A few things are missing (you can still publish):</p>
            <ul className="list-disc pl-5 space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        </div>
      )}

      <div className="max-w-2xl space-y-6">
        {/* Client & headline */}
        <Card title="Client & headline">
          <Field label="Case study title (H1) — lead with the outcome"><input data-testid="case-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="e.g. How we cut month-end from 10 days to 2" /></Field>
          <Field label="Subheading / intro (outcome-led)"><textarea data-testid="case-subheading" rows={2} value={form.subheading} onChange={(e) => set("subheading", e.target.value)} className={inputCls} placeholder="One or two lines summarising the transformation." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Client / business name"><input data-testid="case-client-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className={inputCls} /></Field>
            <Field label="Industry / sector"><input data-testid="case-industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} className={inputCls} placeholder="e.g. Hospitality" /></Field>
          </div>
          <Field label="Location (optional)"><input data-testid="case-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="e.g. Tamworth" /></Field>
          <Field label="Card excerpt (short description shown on the listing card)"><textarea data-testid="case-summary" rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} className={inputCls} /></Field>
          <ImageUpload label="Featured image" value={form.image || ""} onChange={(v) => set("image", v)} altValue={form.image_alt} onAltChange={(v) => set("image_alt", v)} />
          <ImageUpload label="Client logo (optional)" value={form.logo || ""} onChange={(v) => set("logo", v)} aspect="aspect-[3/1]" />
        </Card>

        {/* Story sections */}
        <Card title="The story" subtitle="Each section is optional — empty sections are hidden on the page. Use **bold**, ## Heading and [links](/services/payroll); one paragraph per line.">
          <div className="flex items-center justify-between gap-3 -mt-1 mb-1">
            <p className="text-xs text-slate-400">Not sure where to start? Drop in a ready-made structure.</p>
            <TemplatePicker
              kind="case_study"
              label="Fill with template"
              hasContent={() => Object.values(form.section_bodies || {}).some((v) => (v || "").trim())}
              onApply={(t) => set("section_bodies", { ...(t.sections || {}) })}
            />
          </div>
          {SECTIONS.map((s) => (
            <div key={s.key} className="border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <label className="text-sm font-semibold text-navy">{s.label}</label>
                <input data-testid={`case-heading-${s.key}`} value={form.section_headings?.[s.key] ?? ""} onChange={(e) => setHeading(s.key, e.target.value)} placeholder={`Heading (default: ${s.label})`} className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-56 text-slate-500" />
              </div>
              <p className="text-xs text-slate-400 mb-1.5">{s.hint}</p>
              <RichTextEditor testId={`case-section-${s.key}`} value={form.section_bodies?.[s.key] ?? ""} onChange={(v) => setBody(s.key, v)} />
            </div>
          ))}
        </Card>

        {/* Key results */}
        <Card title="Key results" subtitle="Up to four outcomes shown as a visual highlight. A statistic is optional — leave it blank for qualitative wins.">
          {(form.results || []).map((r, i) => (
            <div key={i} className="border border-slate-100 rounded-xl p-4 space-y-2 relative" data-testid={`case-result-${i}`}>
              <button onClick={() => rmResult(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><X size={15} /></button>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Field label="Outcome heading"><input data-testid={`case-result-heading-${i}`} value={r.heading} onChange={(e) => updResult(i, "heading", e.target.value)} className={inputCls} /></Field></div>
                <Field label="Statistic (optional)"><input data-testid={`case-result-stat-${i}`} value={r.stat} onChange={(e) => updResult(i, "stat", e.target.value)} className={inputCls} placeholder="e.g. 80%" /></Field>
              </div>
              <Field label="Short explanation"><input data-testid={`case-result-exp-${i}`} value={r.explanation} onChange={(e) => updResult(i, "explanation", e.target.value)} className={inputCls} placeholder="What this meant for the client." /></Field>
            </div>
          ))}
          {(form.results || []).length < 4 && <button onClick={addResult} data-testid="case-add-result" className="text-sm font-semibold text-teal inline-flex items-center gap-1"><Plus size={14} /> Add result</button>}
        </Card>

        {/* Testimonials */}
        <Card title="Client testimonials" subtitle="Genuine client quotes, shown as entered. Mark one as the Featured Quote to highlight it prominently.">
          {(form.testimonials || []).map((t, i) => (
            <div key={i} className="border border-slate-100 rounded-xl p-4 space-y-2 relative" data-testid={`case-testimonial-${i}`}>
              <button onClick={() => rmTestimonial(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><X size={15} /></button>
              <Field label="Testimonial"><textarea data-testid={`case-testimonial-quote-${i}`} rows={2} value={t.quote} onChange={(e) => updTestimonial(i, "quote", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Name (optional)"><input value={t.name} onChange={(e) => updTestimonial(i, "name", e.target.value)} className={inputCls} /></Field>
                <Field label="Job title (optional)"><input value={t.job_title} onChange={(e) => updTestimonial(i, "job_title", e.target.value)} className={inputCls} /></Field>
                <Field label="Company"><input value={t.company} onChange={(e) => updTestimonial(i, "company", e.target.value)} className={inputCls} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                <input type="radio" name="featured-quote" data-testid={`case-testimonial-featured-${i}`} checked={!!t.featured} onChange={() => setFeaturedTestimonial(i)} className="w-4 h-4 accent-orange" /> Featured quote
              </label>
            </div>
          ))}
          <button onClick={addTestimonial} data-testid="case-add-testimonial" className="text-sm font-semibold text-teal inline-flex items-center gap-1"><Plus size={14} /> Add testimonial</button>
        </Card>

        {/* Share quote card */}
        <Card title="Share quote card (optional)" subtitle="When this story is shared on LinkedIn or WhatsApp, a quote card is shown instead of the photograph. Left empty, the featured testimonial above is used automatically.">
          <Field label="Pull quote">
            <textarea data-testid="case-pull-quote" rows={3} value={form.pull_quote || ""} onChange={(e) => set("pull_quote", e.target.value)} className={inputCls} placeholder="One line worth quoting from this story." />
          </Field>
          <Field label="Who said it">
            <input data-testid="case-quote-by" value={form.quote_by || ""} onChange={(e) => set("quote_by", e.target.value)} className={inputCls} placeholder="Name and business" />
          </Field>
        </Card>

        {/* Video */}
        <Card title="Video testimonial (optional)" subtitle="Only shown when a link or upload is added. The written story stays complete without it.">
          <Field label="YouTube link or ID"><input data-testid="case-video-url" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} placeholder="Paste a YouTube link or ID" /></Field>
          <VideoUpload label="Or upload a video" value={form.video_upload || ""} onChange={(v) => set("video_upload", v)} hint="If both a link and an upload are added, the uploaded video is used." />
        </Card>

        {/* Services */}
        <Card title="Services that helped this client" subtitle="Tick the CASS services involved — they link to the existing service pages, and those pages can show this story in return.">
          {services.length === 0 ? <p className="text-sm text-slate-400">No services found.</p> : (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="case-services">
              {services.map((s) => (
                <label key={s.slug} className="flex items-center gap-2 text-sm text-navy cursor-pointer bg-slate-50 rounded-lg px-3 py-2 hover:bg-slate-100">
                  <input type="checkbox" data-testid={`case-service-${s.slug}`} checked={(form.service_slugs || []).includes(s.slug)} onChange={() => toggleService(s.slug)} className="w-4 h-4 accent-orange shrink-0" />
                  <span className="truncate">{s.title}</span>
                </label>
              ))}
            </div>
          )}
        </Card>

        {/* CTA */}
        <Card title="Call to action" subtitle="Shown at the end of the story. Leave blank to use the standard CASS enquiry CTA.">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Heading"><input data-testid="case-cta-heading" value={form.cta_heading} onChange={(e) => set("cta_heading", e.target.value)} className={inputCls} placeholder="Could your business be next?" /></Field>
            <Field label="Button text"><input data-testid="case-cta-button" value={form.cta_button} onChange={(e) => set("cta_button", e.target.value)} className={inputCls} placeholder="Let's Talk" /></Field>
          </div>
          <Field label="Supporting text"><textarea data-testid="case-cta-text" rows={2} value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} className={inputCls} /></Field>
          <Field label="Button link"><input data-testid="case-cta-link" value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} className={inputCls} placeholder="/contact" /></Field>
        </Card>

        {/* SEO */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <button onClick={() => setSeoOpen((o) => !o)} data-testid="case-seo-toggle" className="w-full flex items-center justify-between p-6">
            <span className="font-display font-bold text-navy">SEO &amp; search visibility</span>
            <ChevronDown size={18} className={`text-slate-400 transition-transform ${seoOpen ? "rotate-180" : ""}`} />
          </button>
          {seoOpen && (
            <div className="px-6 pb-6 space-y-4 border-t border-slate-100 pt-5">
              <Field label={`SEO page title  ·  ${titleLen} chars (aim for 50–60)`}><input data-testid="case-seo-title" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} placeholder="Falls back to the story title" /></Field>
              <Field label={`Meta description  ·  ${metaLen} chars (aim for 150–160)`}><textarea data-testid="case-meta-desc" rows={2} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} className={inputCls} placeholder="Falls back to the card excerpt" /></Field>
              <Field label="URL slug"><input data-testid="case-slug" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inputCls} placeholder="auto-generated from the title if left blank" /></Field>
              <Field label="Canonical URL (optional)"><input data-testid="case-canonical" value={form.canonical_url} onChange={(e) => set("canonical_url", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Social sharing title"><input data-testid="case-og-title" value={form.og_title} onChange={(e) => set("og_title", e.target.value)} className={inputCls} /></Field>
                <Field label="Social sharing description"><input data-testid="case-og-desc" value={form.og_description} onChange={(e) => set("og_description", e.target.value)} className={inputCls} /></Field>
              </div>
              <ImageUpload label="Social sharing image (optional)" value={form.og_image || ""} onChange={(v) => set("og_image", v)} testId="case-og-image" />
              <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                <input type="checkbox" data-testid="case-noindex" checked={!!form.noindex} onChange={(e) => set("noindex", e.target.checked)} className="w-4 h-4 accent-orange" /> Hide from search engines (noindex)
              </label>
            </div>
          )}
        </div>

        {/* Publish controls */}
        <Card title="Publishing">
          <div className="grid grid-cols-2 gap-4 items-center">
            <Field label="Order"><input data-testid="case-order" type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={`${inputCls} w-28`} /></Field>
            <label className="flex items-center gap-2 mt-6 text-sm font-semibold text-navy cursor-pointer">
              <input data-testid="case-published" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-orange" /> Published (visible on site)
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
            <input data-testid="case-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-orange" /> Feature in the homepage hero ticker
          </label>
        </Card>

        <button data-testid="case-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save story"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1.5">{label}</label>
    {children}
  </div>
);
const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4">
    <div>
      <h2 className="font-display font-bold text-navy">{title}</h2>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);
